/**
 * Geração de tabelas LaTeX (Conversão HRN e Plano de Ação)
 */

import { IMAGENS_ESTATICAS } from '../static/latex-static-assets';
import { Risco } from '../core/latex-types';
import { escapeLatex } from '../core/latex-utils';

/**
 * Determina a classificação e cor baseada no HRN - Tabela Cliente (Arcelor Mittal)
 */
function obterClassificacaoHRN(hrn?: number): {
  classificacao: string;
  corFundo: string;
} {
  if (!hrn || hrn < 1) {
    return {
      classificacao: 'Aceitável',
      corFundo: '[HTML]{D2FFD2}'
    };
  }

  if (hrn >= 1000) {
    return {
      classificacao: 'Inaceitável',
      corFundo: '[HTML]{FF9999}'
    };
  } else if (hrn >= 500) {
    return {
      classificacao: 'Extremo',
      corFundo: '[HTML]{FF9999}'
    };
  } else if (hrn >= 100) {
    return {
      classificacao: 'Muito alto',
      corFundo: '[HTML]{FF9999}'
    };
  } else if (hrn >= 50) {
    return {
      classificacao: 'Alto',
      corFundo: '[HTML]{FFFF99}'
    };
  } else if (hrn >= 10) {
    return {
      classificacao: 'Significante',
      corFundo: '[HTML]{FFFF99}'
    };
  } else if (hrn >= 5) {
    return {
      classificacao: 'Baixo',
      corFundo: '[HTML]{FFFF99}'
    };
  } else {
    return {
      classificacao: 'Muito baixo',
      corFundo: '[HTML]{D2FFD2}'
    };
  }
}

/**
 * Remove comandos \textbf{...} do texto, mantendo apenas o conteúdo interno
 * @param text Texto que pode conter comandos \textbf{...}
 * @returns Texto sem os comandos \textbf{...}
 */
function removerNegritos(text: string): string {
  if (!text) return text;

  // Remover \textbf{...} recursivamente (pode haver negritos aninhados)
  let resultado = text;
  let mudou = true;

  while (mudou) {
    const antes = resultado;
    // Remover \textbf{conteudo} substituindo por apenas conteudo
    resultado = resultado.replace(/\\textbf\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, '$1');
    mudou = antes !== resultado;
  }

  return resultado;
}

/**
 * Processa medidas de controle convertendo linhas que começam com "+" em listas LaTeX
 * Remove negritos de todos os itens
 * Retorna string formatada para uso em células de tabela
 */
function processarMedidasControleParaTabela(medidasControle: string): string {
  if (!medidasControle) return 'Não especificado';

  const linhas = medidasControle.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const resultado: string[] = [];
  let currentItem: string | null = null;
  const subitems: string[] = [];

  for (const linha of linhas) {
    // Remover negritos da linha antes de processar
    const linhaSemNegrito = removerNegritos(linha);

    if (linhaSemNegrito.startsWith('+')) {
      // É um subitem - remover o "+" e adicionar aos subitens do item atual
      const subitemText = linhaSemNegrito.substring(1).trim();
      if (subitemText) {
        subitems.push(subitemText);
      }
    } else {
      // É um item principal - primeiro salvar o item anterior com seus subitens
      if (currentItem !== null) {
        if (subitems.length > 0) {
          const subitemsLatex = subitems.map(s => `\\item ${escapeLatex(removerNegritos(s))}`).join('\n');
          resultado.push(`${escapeLatex(removerNegritos(currentItem))}\n\\begin{itemize}[leftmargin=*,itemsep=0pt,parsep=0pt,topsep=2pt]\n${subitemsLatex}\n\\end{itemize}`);
        } else {
          resultado.push(escapeLatex(removerNegritos(currentItem)));
        }
        subitems.length = 0; // Limpar array
      }
      currentItem = linhaSemNegrito;
    }
  }

  // Adicionar o último item
  if (currentItem !== null) {
    if (subitems.length > 0) {
      const subitemsLatex = subitems.map(s => `\\item ${escapeLatex(removerNegritos(s))}`).join('\n');
      resultado.push(`${escapeLatex(removerNegritos(currentItem))}\n\\begin{itemize}[leftmargin=*,itemsep=0pt,parsep=0pt,topsep=2pt]\n${subitemsLatex}\n\\end{itemize}`);
    } else {
      resultado.push(escapeLatex(removerNegritos(currentItem)));
    }
  }

  // Se não houver itens processados, retornar o texto original escapado (sem negritos)
  if (resultado.length === 0) {
    return escapeLatex(removerNegritos(medidasControle));
  }

  // Juntar com \par para quebras entre itens principais
  return resultado.join(' \\par ');
}

/**
 * Gera a tabela de conversão de classificação HRN (segunda página)
 * Usa uma imagem ao invés de gerar a tabela em LaTeX
 */
export function gerarTabelaConversaoHRN(): string {
  return `
\\newpage

\\begin{center}

\\textbf{\\Large TABELA DE CONVERSÃO DE CLASSIFICAÇÃO HRN}

\\end{center}

\\vspace{0.4cm}

\\begin{center}
\\includegraphics[width=\\textwidth]{${IMAGENS_ESTATICAS.TABELA_CONVERSAO}}
\\end{center}

\\newpage

`;
}

/**
 * Consolida medidas de controle de múltiplos riscos em um único texto
 * Remove duplicatas e mantém a estrutura original
 */
function consolidarMedidasControle(riscos: Risco[]): string {
  const todasMedidas: string[] = [];

  riscos.forEach(risco => {
    const medidas = risco.medidasControle || risco.adequacoesTecnicas;
    if (medidas && medidas.trim()) {
      todasMedidas.push(medidas.trim());
    }
  });

  if (todasMedidas.length === 0) {
    return 'Não especificado';
  }

  // Juntar todas as medidas, separando por quebra de linha dupla para distinguir origens diferentes
  // Mas se houver duplicatas exatas, remover
  const medidasUnicas = Array.from(new Set(todasMedidas));

  return medidasUnicas.join('\n\n');
}

/**
 * Gera a tabela de Plano de Ação (terceira página)
 * Agrupa riscos por parte e mostra apenas partes não conformes com suas medidas de controle
 * Caso não haja riscos, retorna tabela estática padrão (para compatibilidade)
 */
export function gerarPlanoAcao(riscos?: Risco[]): string {
  // Se não houver riscos, retornar tabela estática padrão (compatibilidade)
  if (!riscos || riscos.length === 0) {
    return `
\\newpage

% ===================== TÍTULO =====================
\\begin{center}

{\\bfseries\\Large PLANO DE AÇÃO PARA Apreciações de riscos}

\\end{center}

\\vspace{0.4cm}

% ===================== TABELA =====================
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{6pt}

\\begin{flushleft}
\\begin{tabularx}{\\linewidth}{|>{\\centering\\arraybackslash}p{1.5cm}|>{\\raggedright\\arraybackslash}X|>{\\centering\\arraybackslash}p{1.5cm}|>{\\centering\\arraybackslash}p{2.5cm}|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{N° Perigo}} &
\\textcolor{white}{\\textbf{Ação (medidas de controle)}} &
\\textcolor{white}{\\textbf{HRN (atual)}} &
\\textcolor{white}{\\textbf{Classificação (De risco)}} \\\\
\\hline

3.1 &
Adotar redundância dos dispositivos responsáveis pela função de parada relacionada à segurança, conforme categoria de segurança, do acionamento do motor elétrico. &
- &
\\cellcolor[HTML]{FFFF99}\\textbf{ALTO} \\\\
\\hline

4.1 &
Adotar sistema de proteção fixa internamente à chapa instalada de modo que restrinja o acesso à zona de perigo identificada, evitando queda, e que ainda possibilite inspeção visual.



A proteção fixa interna somente pode ser retirada com equipamento bloqueado.



\\textbf{Obs.:} O sistema deve ser resistente tanto ao ambiente do local como ao mecanismo. &
- &
\\cellcolor[HTML]{FFFF99}\\textbf{ALTO} \\\\
\\hline

4.2 &
Adotar sistema de proteção fixa de modo que restrinja o acesso por todos os lados à zona de perigo identificada. A proteção fixa somente pode ser retirada com equipamento bloqueado. &
- &
\\cellcolor[HTML]{FFFF99}\\textbf{ALTO} \\\\
\\hline

4.3 &
Adotar sistema de proteção fixa e porta de proteção com dispositivo de intertravamento de modo que restrinja o acesso por todos os lados à zona de perigo identificada. O acesso só pode ser permitido após cessados os movimentos perigosos. A proteção deve ser resistente às condições ambientais locais. &
- &
\\cellcolor[HTML]{FFFF99}\\textbf{ALTO} \\\\
\\hline

4.4 &
Adotar sistema de proteção fixa de modo que restrinja o acesso por todos os lados à zona de perigo identificada. A proteção fixa somente pode ser retirada com equipamento bloqueado. &
- &
\\cellcolor[HTML]{FFFF99}\\textbf{ALTO} \\\\
\\hline


5.2 &
Tornar o dispositivo de parada de emergência disponível e operacional a qualquer tempo, independentemente do modo de operação. Recomenda-se também a remoção do antigo dispositivo para que possa ser acionado sem qualquer dificuldade e que o movimento de emergência seja permitido apenas com a palma da mão.



\\textbf{Obs.:} Os dispositivos de parada de emergência instalados devem atender à categoria de segurança. &
- &
\\cellcolor[HTML]{FFFF99}\\textbf{ALTO} \\\\
\\hline

111 &
Adotar sinalização de segurança para advertir os trabalhadores e terceiros sobre os riscos aos quais estão expostos. A sinalização deve ficar destacada na máquina em local visível, ser legível e redigida em língua portuguesa. As inscrições devem indicar claramente o risco e a parte danificada a que se referem. &
- &
\\cellcolor[HTML]{D2FFD2}\\textbf{BAIXO} \\\\
\\hline

121 &
As máquinas e equipamentos devem possuir manual de instruções fornecido pelo fabricante ou importador, contemplando todas as fases e segurança das etapas de utilização.

Quando inexistente ou extraviado, o manual deve ser reconstituído pelo empregador com pessoal por ele designado, sob responsabilidade de profissional qualificado ou engenheiro habilitado.

Os manuais reconstituídos devem conter, no mínimo:

\\begin{itemize}[leftmargin=*,itemsep=0pt,parsep=0pt,topsep=2pt]
\\fontsize{9pt}{10.8pt}\\selectfont
\\item Tipo, modelo e capacidade;
\\item Descrição detalhada da máquina ou equipamento e seus acessórios;
\\item Definição da utilização prevista e limitações de uso;
\\item Definição das medidas de segurança existentes e das que devem ser adotadas pelos usuários;
\\item Especificações e limitações técnicas para utilização dos dispositivos de segurança;
\\item Riscos que podem ser gerados e suas respectivas fontes;
\\item Informações técnicas para subsidiar a elaboração de procedimentos seguros em todas as fases de utilização.
\\end{itemize} &
- &
\\cellcolor[HTML]{D2FFD2}\\textbf{BAIXO} \\\\
\\hline

\\end{tabularx}
\\end{flushleft}

\\newpage

`;
  }

  // Agrupar riscos por parte
  const riscosPorParte = new Map<string, Risco[]>();

  riscos.forEach(risco => {
    const parte = risco.parte || 'Sem parte definida';
    if (!riscosPorParte.has(parte)) {
      riscosPorParte.set(parte, []);
    }
    riscosPorParte.get(parte)!.push(risco);
  });

  // Filtrar apenas partes com riscos não conformes
  const partesNaoConformes = new Map<string, Risco[]>();

  riscosPorParte.forEach((riscosDaParte, parte) => {
    const riscosNaoConformes = riscosDaParte.filter(r => r.conformeAtual === false);
    if (riscosNaoConformes.length > 0) {
      partesNaoConformes.set(parte, riscosNaoConformes);
    }
  });

  // Se não houver partes não conformes, retornar tabela vazia
  if (partesNaoConformes.size === 0) {
    return `
% ===================== TABELA =====================
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{6pt}

\\begin{flushleft}
\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}p{2.5cm}|>{\\raggedright\\arraybackslash}X|>{\\centering\\arraybackslash}p{2.5cm}|>{\\centering\\arraybackslash}p{1.5cm}|>{\\centering\\arraybackslash}p{2.5cm}|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Parte}} &
\\textcolor{white}{\\textbf{Medidas de Controle}} &
\\textcolor{white}{\\textbf{Conformidade}} &
\\textcolor{white}{\\textbf{HRN (atual)}} &
\\textcolor{white}{\\textbf{Classificação (De risco)}} \\\\
\\hline
\\end{tabularx}
\\end{flushleft}

`;
  }

  // Gerar linhas da tabela agrupadas por parte
  const linhasTabela: string[] = [];

  partesNaoConformes.forEach((riscosNaoConformes, parte) => {
    // Consolidar medidas de controle de todos os riscos não conformes da parte
    const medidasConsolidadas = consolidarMedidasControle(riscosNaoConformes);
    const medidasFormatadas = processarMedidasControleParaTabela(medidasConsolidadas);

    // Encontrar o HRN mais alto entre os riscos não conformes da parte
    let hrnMaximo = 0;
    riscosNaoConformes.forEach(risco => {
      const hrn = risco.hrnAtual ?? 0;
      if (hrn > hrnMaximo) {
        hrnMaximo = hrn;
      }
    });

    // Obter classificação e cor baseada no HRN máximo
    const { classificacao, corFundo } = obterClassificacaoHRN(hrnMaximo);

    linhasTabela.push(`${escapeLatex(parte)} &
${medidasFormatadas} &
Não conforme &
${hrnMaximo} &
\\cellcolor${corFundo}\\textbf{${escapeLatex(classificacao)}} \\\\
\\hline`);
  });

  return `
% ===================== TABELA =====================
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{6pt}

\\begin{flushleft}
\\begin{xltabular}{\\linewidth}{|>{\\raggedright\\arraybackslash}p{2.5cm}|>{\\raggedright\\arraybackslash}X|>{\\centering\\arraybackslash}p{2.5cm}|>{\\centering\\arraybackslash}p{1.5cm}|>{\\centering\\arraybackslash}p{2.5cm}|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Parte}} &
\\textcolor{white}{\\textbf{Medidas de Controle}} &
\\textcolor{white}{\\textbf{Conformidade}} &
\\textcolor{white}{\\textbf{HRN (atual)}} &
\\textcolor{white}{\\textbf{Classificação (De risco)}} \\\\
\\hline
\\endfirsthead

\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Parte}} &
\\textcolor{white}{\\textbf{Medidas de Controle}} &
\\textcolor{white}{\\textbf{Conformidade}} &
\\textcolor{white}{\\textbf{HRN (atual)}} &
\\textcolor{white}{\\textbf{Classificação (De risco)}} \\\\
\\hline
\\endhead

\\hline
\\endfoot

\\hline
\\endlastfoot

${linhasTabela.join('\n\n')}

\\end{xltabular}
\\end{flushleft}

`;
}

/**
 * Gera a tabela de análise de risco conforme ABNT NBR 14153
 * Tabela com classificação de segurança (SRP/CS)
 */
